import { FC, Fragment, JSX, useContext, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { MemberSetModel } from "@app/members";
import EditProjectOverrideMemberSet from "@app/projects/editProjectOverrideMemberSet";
import { ProjectOverrideMemberSetType } from "@app/projects/enumerators";
import Project from "@app/projects/project";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import Roles from "@app/user/roles";
import { UserContext, withUserContext } from "@app/user/UserContext";

interface MemberTableProps extends RouteComponentProps {
  disabled: boolean;
  generateAlternateMemberships: () => void;
  handleMemberPopAliasChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopAliasChange2: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMemberPopChange2: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleRiskPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onAddAlternateRow: () => void;
  onAlternateMemberPopAliasChange: (rows: EditProjectOverrideMemberSet[]) => void;
  onAlternateMemberPopChange: (memberPopId: number, prevMemberPopId: number) => void;
  onDeleteAlternateRow: (rows: EditProjectOverrideMemberSet[]) => void;
  project: Project;
  projectMemberPopulations: MemberSetModel[];
  shared: boolean;
  allowAlternates: boolean;
  isZipProject: boolean;
}

interface MemberWaffleToppings {
  key: string;
  MemberPop: JSX.Element;
  MemberAlias: JSX.Element;
  MemberType: JSX.Element;
  Delete: JSX.Element;
}

/* oxlint-disable sort-keys */
const MemberTable: FC<MemberTableProps> = (props: MemberTableProps) => {
  const user = useContext(UserContext);
  const [memberData] = useState(props.project.editProjectOverrideMemberSets);

    const editorConfig = {
      MemberType: {
        displayText: "Member Population Type",
        noSort: true,
        testId: "member-type",
      },
      MemberPop: {
        displayText: "Member Population",
        noSort: true,
        testId: "member-pop",
      },
      MemberAlias: {
        displayText: "Alias",
        noSort: true,
        testId: "member-alias",
      },
      Delete: {
        displayText: " ",
        noSort: true,
        hidden: !props.allowAlternates,
        testId: "delete",
      },
    };

    const viewerConfig = {
      MemberType: {
        displayText: "Member Population Type",
        noSort: true,
        testId: "member-type",
      },
      MemberPop: {
        displayText: "Member Population",
        noSort: true,
        testId: "member-pop",
      },
      MemberAlias: {
        displayText: "Alias",
        noSort: true,
        testId: "member-alias",
      },
    };

    const columnConfig = !props.shared
      ? editorConfig
      : viewerConfig;

    let selectedMembersetsLength = props.project.editProjectOverrideMemberSets.filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate).length;
    if (!!props.project.memberSetId)
      selectedMembersetsLength += 1;
    if (!!props.project.memberSetId2)
      selectedMembersetsLength += 1;
    const allAlternateMembershipsAreUsed = selectedMembersetsLength >= props.projectMemberPopulations.length;

    const addMemberRow = () => {
    props.onAddAlternateRow();
  }

  const deleteMemberRow = (row: EditProjectOverrideMemberSet) => {
    const updateRows = memberData;
    updateRows.splice(memberData.indexOf(row), 1);
    props.onDeleteAlternateRow(updateRows);
  }

  const updateRowAlias = (alias: string, row: EditProjectOverrideMemberSet) => {
    row.alias = alias;
    props.onAlternateMemberPopAliasChange(memberData);
  }

  const updateRowId = (e: React.ChangeEvent<HTMLSelectElement>, prevMemberSetId: number) => {
    const memberSetId = +(e.target.value);
    props.onAlternateMemberPopChange(memberSetId, prevMemberSetId);
    props.generateAlternateMemberships();
  }

  const disableAlias = () => !props.project.memberSetId2;

  const buildRecords = () => {
    const records: MemberWaffleToppings[] = memberData.map((sr) => {
      if (props.isZipProject
        && sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        return;
      }

      return {
        [wafflesKeyColumnName]: sr.projectOverrideMemberSetType.toString(),
        MemberType: (() => {
          return (
            <label className="summary-tile__title">
              {sr.projectOverrideMemberSetType}
            </label>
          );
        })(),
        MemberPop: (() => {
          const usedMemberSetIds = props.project.editProjectOverrideMemberSets
            .filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate)
            .map((oms) => oms.memberSetId)
            .concat(props.project.memberSetId, props.project.memberSetId2)
            .filter((oms) => !!oms);

          if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
            if (props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={props.project.memberSetName || ""}
                  testId="member-table__edit-project-primary-member-set-text-box"
                  label=""
                />
              );
            } else {
              if (props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={props.handleMemberPopChange}
                    testId="member-table__edit-project-primary-member-set-select"
                    disabled={props.disabled}
                    value={props.project.memberSetId ? props.project.memberSetId : ""}
                  >
                    <option key="0" value="0">Select a Member Population</option>
                    {props.project.memberSetId == -1 && <option key="-1" value="-1">Different Member Population Being Used</option>}
                    {props.projectMemberPopulations.map((option) => {
                      const optionIsUsedAndNotThisRow = usedMemberSetIds.includes(option.memberSetId) && memberData[memberData.indexOf(sr)].memberSetId !== option.memberSetId;
                      return (
                        option.hasActivatedVersions ?
                          <option key={option.memberSetId} value={option.memberSetId} disabled={optionIsUsedAndNotThisRow}>{option.resource.name}</option> :
                          <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                      );
                    })}
                  </QESSelect>
                );
              }
            }
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
            if (props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={props.project.memberSetName2 || ""}
                  testId="member-table__edit-project-secondary-member-set-text-box"
                  label=""
                />
              );
            } else {
              if (props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Secondary Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={props.handleMemberPopChange2}
                    testId="member-table__edit-project-secondary-member-set-select"
                    disabled={props.disabled}
                    value={props.project.memberSetId2 ? props.project.memberSetId2 : ""}
                  >
                    <option key="" value="">Select a Second Member Population</option>
                    {props.project.memberSetId2 == -1 && props.projectMemberPopulations.length > 0 && <option key="-1" value="-1">Different Second Member Population Being Used</option>}
                    {props.projectMemberPopulations.map((option) => {
                      const optionIsUsedAndNotThisRow = usedMemberSetIds.includes(option.memberSetId) && memberData[memberData.indexOf(sr)].memberSetId !== option.memberSetId;
                      return (
                        option.hasActivatedVersions ?
                          <option key={option.memberSetId} value={option.memberSetId} disabled={optionIsUsedAndNotThisRow}>{option.resource.name}</option> :
                          <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                      )
                    })}
                  </QESSelect>
                );
              } else {
                return;
              }
            }
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
            if (props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={props.project.editProjectOverrideMemberSets.filter((x) => x.memberSetId === memberData[memberData.indexOf(sr)].memberSetId)[0].memberSetName || ""}
                  testId="member-table__edit-project-alternate-member-set-text-box"
                  label=""
                />
              );
            } else {
              if (props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Alternate Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={(e) => updateRowId(e, sr.memberSetId)}
                    value={memberData[memberData.indexOf(sr)].memberSetId
                      ? memberData[memberData.indexOf(sr)].memberSetId
                      : "0"}
                    disabled={props.disabled}
                    testId="member-table__edit-project-alternate-member-set-select"
                  >
                    <option key="0" value="0">Select an Alternate Member Population</option>
                    {props.projectMemberPopulations.map((option) => {
                      const optionIsUsedAndNotThisRow = usedMemberSetIds.includes(option.memberSetId) && memberData[memberData.indexOf(sr)].memberSetId !== option.memberSetId;
                      return (
                        option.hasActivatedVersions ?
                          <option key={option.memberSetId} value={option.memberSetId} disabled={optionIsUsedAndNotThisRow}>{option.resource.name}</option> :
                          <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                      );
                    })}
                  </QESSelect>
                );
              }
            }
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
            if (props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={props.project.membershipRiskMemberSetAlias || ""}
                  testId="member-table__edit-project-risk-analysis-text-box"
                  label=""
                />
              );
            } else {
              if (user.roles.some((r) => r === Roles.MEMBERSHIP_RISK_USER) && props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Risk Analysis Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={props.handleRiskPopChange}
                    testId="member-table__edit-project-risk-analysis-select"
                    disabled={props.disabled}
                    value={props.project.membershipRiskMemberSetId ? props.project.membershipRiskMemberSetId : ""}
                  >
                    <option key="0" value="0">Select a Risk Analysis Member Population</option>
                    {props.projectMemberPopulations.map((option) => (
                      option.hasActivatedVersions ?
                        <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
                        <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                    ))}
                  </QESSelect>
                );
              }
            }
          }
        })(),
        MemberAlias: (() => {
          const otherAliases = new Set(props.project.editProjectOverrideMemberSets.filter((oms) => oms.memberSetId !== sr.memberSetId).map((oms) => oms.alias));
          if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
            return (
              <div>
                <QESTextInput
                  className="form-control"
                  disabled={props.disabled}
                  size={25}
                  value={props.project.memberSetAlias == "-1" ? "Different Member Alias Being Used" : props.project.memberSetAlias || ""}
                  testId="member-table__edit-project-primary-member-set-type-alias-input"
                  onChange={props.handleMemberPopAliasChange}
                  label=""
                  hideLabel={true}
                  style={{ padding: 0 }}
                  unrequiredValidation={true}
                  isValid={!sr.alias || !otherAliases.has(sr.alias)}
                  wasFocusedExternally={true}
                />
              </div>
            );
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
            return (
              <div>
                <QESTextInput
                  className="form-control"
                  disabled={props.disabled || disableAlias()}
                  size={25}
                  value={props.project.memberSetAlias2 == "-1" ? "Different Member Alias Being Used" : props.project.memberSetAlias2 || ""}
                  testId="member-table__edit-project-secondary-member-set-type-alias-input"
                  onChange={props.handleMemberPopAliasChange2}
                  label=""
                  hideLabel={true}
                  style={{ padding: 0 }}
                  unrequiredValidation={true}
                  isValid={!sr.alias || !otherAliases.has(sr.alias)}
                  wasFocusedExternally={true}
                />
              </div>
            );
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
            return (
              <div>
                <QESTextInput
                  className="form-control"
                  disabled={props.disabled}
                  maxLength={255}
                  value={sr.alias || ""}
                  testId="member-table__edit-project-alternate-member-set-type-alias-input"
                  onChange={(e) => updateRowAlias(e.target.value, sr)}
                  label="Alias"
                  hideLabel={true}
                  style={{ padding: 0 }}
                  unrequiredValidation={true}
                  isValid={!sr.alias || !otherAliases.has(sr.alias)}
                  wasFocusedExternally={true}
                />
              </div>
            );
          }
        })(),
        Delete: (() => (
          <div style={{ minWidth: "1rem" }}>
            {(!props.shared && sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) && (
              <QESButton
                qesType="secondary-danger"
                className="p-0"
                qesTestId="member-table__edit-project-delete-alternate-member-set-type-alias-button"
                onClick={() => deleteMemberRow(sr)}
              >
                <QesIcon iconName={QesIcons.TrashV1} description="Remove" height="18" className="text-danger" />
              </QESButton>
            )}
          </div>
        ))(),
      };
    });

    return records.filter((r) => !!r);
  }

  const records = buildRecords();

    return (
      <Fragment>
        <Waffles<MemberWaffleToppings>
          columns={columnConfig}
          data={records}
          disableProcessingOverlay={true}
          testId="member-view"
        />
        {!props.shared && props.allowAlternates ?
          <QESButton
            qesTestId="member-table_add-alternate-member-population"
            qesType="secondary"
            onClick={() => addMemberRow()}
            disabled={props.disabled || allAlternateMembershipsAreUsed}
          ><QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top" /> Add Alternate Member Population</QESButton> :
          <div />
        }
      </Fragment>
    );
}

const WrappedMemberTable = withUserContext(MemberTable);

export { WrappedMemberTable as MemberTable };
